import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap} from "gsap";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {GameAssets} from "../GameAssets";
import {Game} from "../Game";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {GameUtils} from "../utils/GameUtils";
import {Sound} from "../sound/Sound";

export class BigWinView extends PIXI.Container implements StageLayer {

    private _countUp: BasicBitmapCountUp;
    private _blackBg: PIXI.Graphics;
    private _bigWinLevels: PIXI.spine.Spine[] = [];
    private _oldScreenSpine: PIXI.spine.Spine;
    private _oldScreenTl: gsap.core.Timeline | null;
    // #326
    private _censoredBg: PIXI.Graphics;
    private _censoredText: PIXI.Text;
    private _maxLevel: number = 1;

    constructor() {
        super();
        this.name = "BigWinView";
        this.init();
    }

    public playBlackBg(): void {
        this._blackBg.visible = true;
    }

    public getLevelAnimation(countFrom: number, countTo: number, duration: number, level: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        const currentBWSpine: PIXI.spine.Spine = this._bigWinLevels[level - 1];
        currentBWSpine.alpha = 0;
        GameUtils.resetSpineItem(currentBWSpine);
        currentBWSpine.position.set(360, -currentBWSpine.height * 0.5);
        tl.add(() => {
            Game.soundPlayer.playBigWinSound(level);
        }, 0);

        if ((level === 1) || (Game.model.gameData.wasFeatureBuy && level === 2)) {
            tl.add(() => {
                this.alpha = 1;
                this._countUp.scale.set(0);
                this._countUp.position.y = NolimitApplication.isLandscape ? 580 : 640;
                this._countUp.stopAt(0);
                this._countUp.visible = true;
            }, 0);
            tl.to(this._countUp.scale, {x: 1, y: 1, duration: 0.3}, "<");
            tl.to(this._countUp, {alpha: 1, duration: 0.3}, "<");
        }

        tl.add([
            () => {
                this.addChild(currentBWSpine, this._oldScreenSpine);
            },
            this._countUp.getCountAnimation(countFrom, countTo, duration),
            GameUtils.createTimelineForAnimation(currentBWSpine, 0, "animation", false)
        ], 0);

        // #326
        let censored: boolean = false;
        if (level > 3 && (Game.apiPlugin.gameClientConfiguration.useCensoredGfx || Game.apiPlugin.gameClientConfiguration.useCensoredGfxUS)) {
            censored = true;
            tl.add(() => {
                this.addChild(this._censoredBg, this._censoredText);

                this.stopOldScreenEffect();
                this._oldScreenSpine.visible = false;
            }, 0);
        } else {
            tl.add(this.shake(currentBWSpine, 3, 3, 25, 1, duration - 0.3), 0.3);
        }
        tl.add([
            this.transitionAnimation(currentBWSpine, level - 1, censored),
            () => {
                this.addChild(this._countUp);
            }
        ], 0);

        tl.add(this.shake(this._countUp, 1, 1, 70, 4.5, duration * 0.4), duration * 0.6);
        tl.add(() => {
            this._countUp.stopAt(countTo);
        });

        return tl;
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this._countUp.position.set(360, 580);
            this._oldScreenSpine.scale.set(0.71);
            this._bigWinLevels.forEach((bigWinLevel: PIXI.spine.Spine) => {
                bigWinLevel.scale.set(0.7);
            })
        } else {
            this._countUp.position.set(360, 640);
            this._oldScreenSpine.scale.set(0.65);
            this._bigWinLevels.forEach((bigWinLevel: PIXI.spine.Spine) => {
                bigWinLevel.scale.set(0.65);
            })
        }
        this.blackBgSetOrientation();
    }

    public hide(duration: number = 1.0): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        tl.add(() => {
            for (let i: number = 0; i < this._maxLevel - 1; i++) {
                this._bigWinLevels[i].visible = false;
            }

            // #326
            if (this.children.indexOf(this._censoredBg) > -1) {
                this._bigWinLevels[this._maxLevel - 1].visible = false;
            }
            this.removeChild(this._censoredText);
        }, 0);
        tl.add(gsap.to(this._countUp, {duration: 0.2, y: 360}), 0);
        tl.to(this, {duration: duration, alpha: 0}, 1.5);

        tl.add(() => {
            this.removeChild(this._censoredBg);

            this._oldScreenSpine.visible = false;
            this._bigWinLevels[this._maxLevel - 1].visible = false;
            this.visible = false;
        }, duration + 1.5);
        return tl;
    }

    public killSpine(spine: PIXI.spine.Spine): void {
        spine.visible = false;
        spine.state.clearTracks();
        spine.skeleton.setToSetupPose();
        spine.skeleton.setSlotsToSetupPose();
        spine.state.setEmptyAnimation(0, 0);
    }

    public shake(content: BasicBitmapCountUp | PIXI.spine.Spine, x: number, y: number, hertz: number, intensity: number, duration: number): gsap.core.Timeline {
        let startPosY: number,
            startPosX: number,
            t: boolean = true;
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                startPosY = content.y;
                startPosX = content.x;
            },
            onComplete: () => {
                content.x = 360;
                if (content === this._countUp) {
                    this._countUp.y = NolimitApplication.isLandscape ? 580 : 640;
                } else {
                    content.y = 360;
                }
            },
            onUpdate: () => {
                const lt: number = content === this._countUp ? tl.time() : Math.random() * 2,
                    et: number = content === this._countUp ? tl.progress() : Math.random();
                if (t) {
                    t = false;
                    return;
                }

                if (y !== 0) {
                    content.y = startPosY + Math.cos(lt * hertz) * (intensity * et) * y;
                }
                if (x !== 0 && content === this._countUp) {
                    content.x = startPosX + (Math.sin(lt * hertz) * (intensity * et)) * x;
                }
            }
        });

        tl.to(this, {duration: duration});

        return tl;
    }

    public playOldScreenEffect(): gsap.core.Timeline {
        this._oldScreenTl = gsap.timeline();
        this._oldScreenTl.add(() => {
            this._oldScreenSpine.visible = true;
            this._oldScreenSpine.state.setAnimation(0, "desktop", true);
        });
        return this._oldScreenTl;
    }

    public stopOldScreenEffect(): void {
        this._oldScreenTl?.pause();
        this._oldScreenTl?.kill();
    }

    private transitionAnimation(bigWinSpine: PIXI.spine.Spine, prevLevel: number, censored: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            Game.soundPlayer.playSound(Sound.BIGWIN_LEVEL_CHANGE);
        }, 0);
        if (bigWinSpine.name === "bgLevel6") {
            tl.add(() => {
                bigWinSpine.position.y = 360;
            }, 0)
            tl.to(bigWinSpine, {alpha: 1, duration: 0.3})
        } else {
            tl.to(bigWinSpine, {duration: (censored ? 0.01 : 0.3), y: 360, alpha: 1});
            if (prevLevel !== 0) {
                tl.to(this._bigWinLevels[prevLevel - 1], {
                    duration: (censored ? 0.01 : 0.3),
                    y: 360 + this._bigWinLevels[prevLevel - 1].height * 0.5,
                    alpha: 0
                }, "<");
            }
        }

        return tl;
    }

    private init(): void {
        const container: PIXI.Container = new PIXI.Container();

        this._countUp = new BasicBitmapCountUp({
            fontName: GameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 52,
        }, 580, new PIXI.Point(0.5, 0.25));
        this._countUp.visible = false;

        this._blackBg = new PIXI.Graphics();
        this.blackBgSetOrientation();
        this._blackBg.visible = false;

        this._oldScreenSpine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("oldScreenEffect"));
        this._oldScreenSpine.position.set(360);
        this._oldScreenSpine.name = "oldScreenEffect";
        this._oldScreenSpine.scale.set(NolimitApplication.isLandscape ? 0.71 : 0.65);
        this._oldScreenSpine.visible = false;

        this.onResize();
        this.addChild(this._blackBg);
        this.createBigWinLevels();
        this.addChild(this._oldScreenSpine, this._countUp);

        // #326
        this._censoredBg = new PIXI.Graphics();
        this._censoredBg.beginFill(0x000000);
        this._censoredBg.drawRect(0, 0, 720, 800);
        this._censoredBg.endFill();
        const cenText: string = Game.apiPlugin.translations.translate("DUE TO EXPLICIT CONTENT, THIS SCENE HAS BEEN CENSORED.");
        this._censoredText = new PIXI.Text(cenText, new PIXI.TextStyle({
            fill: 0xFFFFFF,
            fontWeight: "bold",
            fontSize: 22,
            wordWrap: true,
            wordWrapWidth: 420,
            breakWords: true,
            align: "center"
        }));
        this._censoredText.anchor.set(0.5);
        this._censoredText.position.set(360);

        this.visible = false;
    }

    private getCountUpScaleAnimation(duration: number, ease: string, to: number, from?: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (from != undefined) {
            tl.add([() => {
                this._countUp.scale.set(from, from);
            }]);
        }
        tl.to(this._countUp.scale, {duration: duration, x: to, y: to, ease: ease});
        return tl;
    }

    private createBigWinLevels(): void {
        const totalLevels: number = 5;
        for (let i: number = 1; i <= totalLevels; i++) {
            let spine: PIXI.spine.Spine = new PIXI.spine.Spine(Game.assetLoader.getSpineAsset("bgLevel" + i));
            spine.visible = false;
            spine.name = "bgLevel" + i;
            spine.scale.set(NolimitApplication.isLandscape ? 0.7 : 0.65);
            this.addChild(spine);
            this._bigWinLevels.push(spine);
        }
    }

    private blackBgSetOrientation() {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        this._blackBg.clear();
        this._blackBg.beginFill(0x000000);
        this._blackBg.drawRect(bounds.left, bounds.top, bounds.width, bounds.height);
        this._blackBg.endFill();
    }

    set maxLevel(value: number) {
        this._maxLevel = value;
    }
}